import { createLocalDocument, type LocalDocument } from "./documents";

const DRIVE_SCOPE = "https://www.googleapis.com/auth/drive.file";
const DRIVE_API_BASE = "https://www.googleapis.com/drive/v3";
const DRIVE_UPLOAD_BASE = "https://www.googleapis.com/upload/drive/v3";
const GOOGLE_DOC_MIME_TYPE = "application/vnd.google-apps.document";
const PLAIN_TEXT_MIME_TYPE = "text/plain";
const FOLDER_MIME_TYPE = "application/vnd.google-apps.folder";
const DRIVE_FILE_FIELDS = "id,name,mimeType,createdTime,modifiedTime,appProperties";
const APP_PROPERTY_KEY = "speechdraft";
const LEGACY_APP_PROPERTY_KEY = "whisperwriter";
const DOCUMENT_PROPERTY_VALUE = "document";
const FOLDER_PROPERTY_VALUE = "folder";
const DOCUMENT_FORMAT_VERSION = "1";
const DRIVE_FOLDER_NAME = "SpeechDraft";
const DRIVE_MAX_RETRY_ATTEMPTS = 3;
const DRIVE_RETRY_BASE_MS = 800;
const DRIVE_RETRY_MAX_MS = 8_000;

interface GoogleTokenResponse {
  access_token?: string;
  expires_in?: number;
  error?: string;
  error_description?: string;
}

interface GoogleTokenClient {
  requestAccessToken(options?: { prompt?: "" | "consent" | "select_account" }): void;
}

interface GoogleAccountsOAuth2 {
  initTokenClient(config: {
    client_id: string;
    scope: string;
    callback: (response: GoogleTokenResponse) => void;
    error_callback?: (error: { type?: string; message?: string }) => void;
  }): GoogleTokenClient;
}

interface DriveFileMetadata {
  id: string;
  name?: string;
  mimeType?: string;
  createdTime?: string;
  modifiedTime?: string;
  appProperties?: Record<string, string>;
}

interface DriveFileListResponse {
  files?: DriveFileMetadata[];
  nextPageToken?: string;
}

interface DriveErrorResponse {
  error?: {
    errors?: Array<{
      reason?: string;
    }>;
    message?: string;
    code?: number;
    status?: string;
  };
}

interface DriveErrorDetails {
  message: string;
  reason: string;
  status: string;
  retryAfterMs?: number;
}

interface LegacyDriveDocumentPayload {
  format: "speechdraft-document" | "whisperwriter-document";
  version: number;
  document: Omit<LocalDocument, "driveFileId">;
}

declare global {
  interface Window {
    google?: {
      accounts?: {
        oauth2?: GoogleAccountsOAuth2;
      };
    };
  }
}

let googleIdentityScriptPromise: Promise<void> | null = null;
let accessToken = "";
let accessTokenExpiresAt = 0;
let driveFolderId = "";
const driveMimeTypeCache = new Map<string, string>();

export function isGoogleDriveConfigured(clientId: string): boolean {
  return Boolean(clientId.trim());
}

export async function requestDriveAccess(clientId: string): Promise<void> {
  if (!isGoogleDriveConfigured(clientId)) {
    throw new Error("Google Drive is not configured.");
  }

  if (!window.google?.accounts?.oauth2) {
    await loadGoogleIdentityScript();
  }

  const oauth = window.google?.accounts?.oauth2;
  if (!oauth) {
    throw new Error("Google Drive sign-in is unavailable.");
  }

  return new Promise((resolve, reject) => {
    const client = oauth.initTokenClient({
      client_id: clientId,
      scope: DRIVE_SCOPE,
      callback: (response) => {
        if (response.error) {
          reject(new Error(response.error_description || response.error));
          return;
        }

        if (!response.access_token) {
          reject(new Error("Google Drive did not return an access token."));
          return;
        }

        accessToken = response.access_token;
        accessTokenExpiresAt = Date.now() + Math.max(0, (response.expires_in ?? 3600) - 60) * 1000;
        resolve();
      },
      error_callback: (error) => {
        reject(
          new Error(
            [error.type, error.message].filter(Boolean).join(": ") ||
              "Google Drive sign-in was cancelled."
          )
        );
      }
    });

    client.requestAccessToken({
      prompt: accessToken && Date.now() < accessTokenExpiresAt ? "" : "consent"
    });
  });
}

export async function listDriveDocuments(): Promise<LocalDocument[]> {
  await ensureDriveFolder();

  const files: DriveFileMetadata[] = [];
  let pageToken = "";

  do {
    const params = new URLSearchParams({
      fields: `nextPageToken,files(${DRIVE_FILE_FIELDS})`,
      pageSize: "100",
      q: `(${getAppPropertyQuery(APP_PROPERTY_KEY, DOCUMENT_PROPERTY_VALUE)} or ${getAppPropertyQuery(LEGACY_APP_PROPERTY_KEY, DOCUMENT_PROPERTY_VALUE)}) and trashed = false`,
      spaces: "drive"
    });

    if (pageToken) {
      params.set("pageToken", pageToken);
    }

    const response = await driveRequest<DriveFileListResponse>(
      `${DRIVE_API_BASE}/files?${params.toString()}`
    );

    files.push(...(response.files ?? []));
    pageToken = response.nextPageToken ?? "";
  } while (pageToken);

  const documents = await Promise.all(files.map(downloadDriveDocument));

  return documents
    .filter((document): document is LocalDocument => Boolean(document))
    .sort((left, right) => right.updatedAt - left.updatedAt);
}

export async function saveDriveDocument(document: LocalDocument): Promise<LocalDocument> {
  const folderId = await ensureDriveFolder();
  const normalizedDocument = createLocalDocument(document);
  const existingDriveFileId = normalizedDocument.driveFileId;
  const existingMimeType = existingDriveFileId
    ? await getDriveFileMimeType(existingDriveFileId)
    : "";
  const shouldUpdateExistingGoogleDoc = existingDriveFileId && existingMimeType === GOOGLE_DOC_MIME_TYPE;
  const targetDriveFileId = shouldUpdateExistingGoogleDoc ? existingDriveFileId : "";
  const metadata: Record<string, unknown> = {
    name: getDriveDocumentName(normalizedDocument),
    appProperties: getDriveDocumentAppProperties(normalizedDocument)
  };

  if (!targetDriveFileId) {
    metadata.mimeType = GOOGLE_DOC_MIME_TYPE;
    metadata.parents = [folderId];
  }

  const payload = getDriveDocumentText(normalizedDocument);
  const fileMetadata = await uploadMultipart<DriveFileMetadata>(
    targetDriveFileId
      ? `${DRIVE_UPLOAD_BASE}/files/${encodeURIComponent(targetDriveFileId)}?uploadType=multipart&fields=${encodeURIComponent(DRIVE_FILE_FIELDS)}`
      : `${DRIVE_UPLOAD_BASE}/files?uploadType=multipart&fields=${encodeURIComponent(DRIVE_FILE_FIELDS)}`,
    targetDriveFileId ? "PATCH" : "POST",
    metadata,
    payload
  );
  driveMimeTypeCache.set(fileMetadata.id, fileMetadata.mimeType ?? GOOGLE_DOC_MIME_TYPE);

  if (existingDriveFileId && existingDriveFileId !== fileMetadata.id) {
    void trashDriveFile(existingDriveFileId).catch(() => {
      // The replacement Google Doc saved successfully; old-file cleanup should not block sync.
    });
  }

  return createLocalDocument({
    ...normalizedDocument,
    driveFileId: fileMetadata.id,
    createdAt: parseDriveTime(fileMetadata.createdTime, normalizedDocument.createdAt),
    updatedAt: parseDriveTime(fileMetadata.modifiedTime, normalizedDocument.updatedAt)
  });
}

export async function deleteDriveDocument(document: LocalDocument): Promise<void> {
  if (!document.driveFileId) {
    return;
  }

  await driveRequest<void>(
    `${DRIVE_API_BASE}/files/${encodeURIComponent(document.driveFileId)}`,
    { method: "DELETE" }
  );
}

export async function deleteDriveDocuments(documents: LocalDocument[]): Promise<void> {
  await Promise.all(documents.map(deleteDriveDocument));
}

export async function preloadGoogleDriveSignIn(clientId: string): Promise<void> {
  if (!isGoogleDriveConfigured(clientId)) {
    return;
  }

  await loadGoogleIdentityScript();
}

async function loadGoogleIdentityScript(): Promise<void> {
  if (window.google?.accounts?.oauth2) {
    return;
  }

  if (googleIdentityScriptPromise) {
    return googleIdentityScriptPromise;
  }

  googleIdentityScriptPromise = new Promise((resolve, reject) => {
    const existingScript = document.querySelector<HTMLScriptElement>(
      'script[src="https://accounts.google.com/gsi/client"]'
    );

    if (existingScript) {
      existingScript.addEventListener("load", () => resolve(), { once: true });
      existingScript.addEventListener("error", () => reject(new Error("Google sign-in failed to load.")), {
        once: true
      });
      return;
    }

    const script = document.createElement("script");
    script.src = "https://accounts.google.com/gsi/client";
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error("Google sign-in failed to load."));
    document.head.appendChild(script);
  });

  return googleIdentityScriptPromise;
}

async function ensureDriveFolder(): Promise<string> {
  if (driveFolderId) {
    return driveFolderId;
  }

  const params = new URLSearchParams({
    fields: "files(id,name,createdTime,modifiedTime,appProperties)",
    pageSize: "1",
    q: `mimeType = '${FOLDER_MIME_TYPE}' and (${getAppPropertyQuery(APP_PROPERTY_KEY, FOLDER_PROPERTY_VALUE)} or ${getAppPropertyQuery(LEGACY_APP_PROPERTY_KEY, FOLDER_PROPERTY_VALUE)}) and trashed = false`,
    spaces: "drive"
  });

  const existingFolder = await driveRequest<DriveFileListResponse>(
    `${DRIVE_API_BASE}/files?${params.toString()}`
  );
  const firstFolder = existingFolder.files?.[0];

  if (firstFolder?.id) {
    driveFolderId = firstFolder.id;
    if (
      firstFolder.name !== DRIVE_FOLDER_NAME ||
      firstFolder.appProperties?.[APP_PROPERTY_KEY] !== FOLDER_PROPERTY_VALUE
    ) {
      void driveRequest<DriveFileMetadata>(
        `${DRIVE_API_BASE}/files/${encodeURIComponent(driveFolderId)}?fields=id,name,appProperties`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json;charset=utf-8"
          },
          body: JSON.stringify({
            name: DRIVE_FOLDER_NAME,
            appProperties: {
              [APP_PROPERTY_KEY]: FOLDER_PROPERTY_VALUE,
              version: DOCUMENT_FORMAT_VERSION
            }
          })
        }
      ).catch(() => {
        // Folder rename is cosmetic; document sync should not fail because of it.
      });
    }
    return driveFolderId;
  }

  const createdFolder = await driveRequest<DriveFileMetadata>(`${DRIVE_API_BASE}/files?fields=id`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json;charset=utf-8"
    },
    body: JSON.stringify({
      name: DRIVE_FOLDER_NAME,
      mimeType: FOLDER_MIME_TYPE,
      appProperties: {
        [APP_PROPERTY_KEY]: FOLDER_PROPERTY_VALUE,
        version: DOCUMENT_FORMAT_VERSION
      }
    })
  });

  driveFolderId = createdFolder.id;
  return driveFolderId;
}

async function downloadDriveDocument(file: DriveFileMetadata): Promise<LocalDocument | null> {
  try {
    driveMimeTypeCache.set(file.id, file.mimeType ?? "");

    if (file.mimeType === GOOGLE_DOC_MIME_TYPE) {
      const payload = await driveTextRequest(
        `${DRIVE_API_BASE}/files/${encodeURIComponent(file.id)}/export?mimeType=${encodeURIComponent(PLAIN_TEXT_MIME_TYPE)}`
      );

      return parseGoogleDocDocument(file, payload);
    }

    const payload = await driveTextRequest(
      `${DRIVE_API_BASE}/files/${encodeURIComponent(file.id)}?alt=media`
    );

    return parseLegacyDriveDocument(file, payload);
  } catch {
    return null;
  }
}

async function getDriveFileMimeType(fileId: string): Promise<string> {
  const cachedMimeType = driveMimeTypeCache.get(fileId);
  if (cachedMimeType !== undefined) {
    return cachedMimeType;
  }

  const file = await driveRequest<DriveFileMetadata>(
    `${DRIVE_API_BASE}/files/${encodeURIComponent(fileId)}?fields=id,mimeType`
  );
  const mimeType = file.mimeType ?? "";
  driveMimeTypeCache.set(fileId, mimeType);

  return mimeType;
}

async function trashDriveFile(fileId: string): Promise<void> {
  driveMimeTypeCache.delete(fileId);

  await driveRequest<void>(
    `${DRIVE_API_BASE}/files/${encodeURIComponent(fileId)}?fields=id`,
    {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json;charset=utf-8"
      },
      body: JSON.stringify({ trashed: true })
    }
  );
}

async function uploadMultipart<T>(
  url: string,
  method: "POST" | "PATCH",
  metadata: Record<string, unknown>,
  payload: string,
  mediaMimeType = PLAIN_TEXT_MIME_TYPE
): Promise<T> {
  const boundary = `speechdraft_${Date.now()}_${Math.random().toString(16).slice(2)}`;
  const body = new Blob(
    [
      `--${boundary}\r\nContent-Type: application/json; charset=UTF-8\r\n\r\n`,
      JSON.stringify(metadata),
      `\r\n--${boundary}\r\nContent-Type: ${mediaMimeType}; charset=UTF-8\r\n\r\n`,
      payload,
      `\r\n--${boundary}--`
    ],
    { type: `multipart/related; boundary=${boundary}` }
  );

  return driveRequest<T>(url, {
    method,
    headers: {
      "Content-Type": `multipart/related; boundary=${boundary}`
    },
    body
  });
}

async function driveRequest<T>(url: string, init: RequestInit = {}): Promise<T> {
  const response = await driveFetch(url, init);

  if (response.status === 204) {
    return undefined as T;
  }

  return (await response.json()) as T;
}

async function driveTextRequest(url: string, init: RequestInit = {}): Promise<string> {
  const response = await driveFetch(url, init);
  return response.text();
}

async function driveFetch(url: string, init: RequestInit = {}): Promise<Response> {
  if (!accessToken || Date.now() >= accessTokenExpiresAt) {
    throw new Error("Google Drive is not connected.");
  }

  let lastError: Error | null = null;

  for (let attempt = 0; attempt <= DRIVE_MAX_RETRY_ATTEMPTS; attempt += 1) {
    const headers = new Headers(init.headers);
    headers.set("Authorization", `Bearer ${accessToken}`);

    try {
      const response = await fetch(url, {
        ...init,
        headers
      });

      if (response.status === 401) {
        accessToken = "";
        accessTokenExpiresAt = 0;
      }

      if (response.ok) {
        return response;
      }

      const errorDetails = await getDriveErrorDetails(response);
      lastError = new Error(errorDetails.message);

      if (
        response.status === 401 ||
        !shouldRetryDriveError(response.status, errorDetails) ||
        attempt >= DRIVE_MAX_RETRY_ATTEMPTS
      ) {
        throw lastError;
      }

      await waitForDriveRetry(getDriveRetryDelay(attempt, errorDetails.retryAfterMs));
    } catch (error) {
      lastError = error instanceof Error ? error : new Error(String(error));

      if (!shouldRetryNetworkError(error) || attempt >= DRIVE_MAX_RETRY_ATTEMPTS) {
        throw lastError;
      }

      await waitForDriveRetry(getDriveRetryDelay(attempt));
    }
  }

  throw lastError ?? new Error("Google Drive request failed.");
}

async function getDriveErrorDetails(response: Response): Promise<DriveErrorDetails> {
  const retryAfterMs = parseRetryAfter(response.headers.get("Retry-After"));

  try {
    const payload = (await response.json()) as DriveErrorResponse;
    const reason = payload.error?.errors?.[0]?.reason ?? payload.error?.status ?? "";

    return {
      message: payload.error?.message || `Google Drive request failed (${response.status}).`,
      reason,
      status: payload.error?.status ?? "",
      retryAfterMs
    };
  } catch {
    return {
      message: `Google Drive request failed (${response.status}).`,
      reason: "",
      status: "",
      retryAfterMs
    };
  }
}

function shouldRetryDriveError(status: number, error: DriveErrorDetails): boolean {
  if (status === 429 || status >= 500) {
    return true;
  }

  if (status !== 403) {
    return false;
  }

  const normalized = `${error.reason} ${error.status} ${error.message}`.toLowerCase();
  return normalized.includes("rate") || normalized.includes("quota");
}

function shouldRetryNetworkError(error: unknown): boolean {
  return error instanceof TypeError;
}

function getDriveRetryDelay(attempt: number, retryAfterMs?: number): number {
  if (retryAfterMs !== undefined) {
    return Math.min(retryAfterMs, DRIVE_RETRY_MAX_MS);
  }

  const exponentialDelay = Math.min(
    DRIVE_RETRY_BASE_MS * 2 ** attempt,
    DRIVE_RETRY_MAX_MS
  );
  const jitter = Math.random() * DRIVE_RETRY_BASE_MS;

  return exponentialDelay + jitter;
}

function parseRetryAfter(value: string | null): number | undefined {
  if (!value) {
    return undefined;
  }

  const seconds = Number(value);
  if (Number.isFinite(seconds)) {
    return Math.max(0, seconds * 1000);
  }

  const timestamp = Date.parse(value);
  if (!Number.isFinite(timestamp)) {
    return undefined;
  }

  return Math.max(0, timestamp - Date.now());
}

function waitForDriveRetry(delayMs: number): Promise<void> {
  return new Promise((resolve) => {
    window.setTimeout(resolve, delayMs);
  });
}

function parseLegacyDriveDocument(file: DriveFileMetadata, payload: string): LocalDocument {
  const trimmedPayload = payload.trimStart();

  if (trimmedPayload.startsWith("{")) {
    const legacyPayload = JSON.parse(trimmedPayload) as LegacyDriveDocumentPayload;
    const document = legacyPayload.document;

    return createLocalDocument({
      ...document,
      driveFileId: file.id,
      createdAt: parseDriveTime(file.createdTime, document.createdAt),
      updatedAt: Math.max(
        document.updatedAt,
        parseDriveTime(file.modifiedTime, document.updatedAt)
      )
    });
  }

  const parsedMarkdown = parseMarkdownDocument(payload);
  const createdAt = parseNumberMetadata(
    parsedMarkdown.metadata.createdAt,
    parseDriveTime(file.createdTime, Date.now())
  );
  const updatedAt = parseNumberMetadata(
    parsedMarkdown.metadata.updatedAt,
    parseDriveTime(file.modifiedTime, createdAt)
  );

  return createLocalDocument({
    id: parseStringMetadata(parsedMarkdown.metadata.id) || file.appProperties?.documentId,
    driveFileId: file.id,
    title: parseStringMetadata(parsedMarkdown.metadata.title) || getTitleFromFileName(file.name),
    text: parsedMarkdown.text,
    createdAt,
    updatedAt: Math.max(updatedAt, parseDriveTime(file.modifiedTime, updatedAt)),
    status: parseDocumentStatus(parsedMarkdown.metadata.status),
    archivedAt: parseOptionalNumberMetadata(parsedMarkdown.metadata.archivedAt),
    trashedAt: parseOptionalNumberMetadata(parsedMarkdown.metadata.trashedAt)
  });
}

function parseGoogleDocDocument(file: DriveFileMetadata, payload: string): LocalDocument {
  const appProperties = file.appProperties ?? {};
  const createdAt = parseNumberStringMetadata(
    appProperties.createdAt,
    parseDriveTime(file.createdTime, Date.now())
  );
  const updatedAt = parseNumberStringMetadata(
    appProperties.updatedAt,
    parseDriveTime(file.modifiedTime, createdAt)
  );

  return createLocalDocument({
    id: appProperties.documentId || file.id,
    driveFileId: file.id,
    title: getTitleFromFileName(file.name),
    text: getDriveDocumentText({ text: payload }),
    createdAt,
    updatedAt: Math.max(updatedAt, parseDriveTime(file.modifiedTime, updatedAt)),
    status: parseDocumentStatus(appProperties.status),
    archivedAt: parseOptionalNumberStringMetadata(appProperties.archivedAt),
    trashedAt: parseOptionalNumberStringMetadata(appProperties.trashedAt)
  });
}

function getDriveDocumentText(document: Pick<LocalDocument, "text">): string {
  return document.text.replace(/\r\n/g, "\n");
}

function getDriveDocumentAppProperties(document: LocalDocument): Record<string, string> {
  return {
    [APP_PROPERTY_KEY]: DOCUMENT_PROPERTY_VALUE,
    documentId: document.id,
    version: DOCUMENT_FORMAT_VERSION,
    createdAt: String(Math.trunc(document.createdAt)),
    updatedAt: String(Math.trunc(document.updatedAt)),
    status: document.status,
    archivedAt: document.archivedAt ? String(Math.trunc(document.archivedAt)) : "",
    trashedAt: document.trashedAt ? String(Math.trunc(document.trashedAt)) : ""
  };
}

function getDriveDocumentName(document: LocalDocument): string {
  const title = document.title.trim() || "Untitled";
  const safeTitle = title.replace(/[^\w .-]+/g, "").replace(/\s+/g, " ").trim() || "Untitled";

  return safeTitle;
}

function parseMarkdownDocument(payload: string): {
  metadata: Record<string, unknown>;
  text: string;
} {
  const normalizedPayload = payload.replace(/\r\n/g, "\n");

  if (!normalizedPayload.startsWith("---\n")) {
    return {
      metadata: {},
      text: normalizedPayload
    };
  }

  const endIndex = normalizedPayload.indexOf("\n---\n", 4);
  if (endIndex === -1) {
    return {
      metadata: {},
      text: normalizedPayload
    };
  }

  const metadataLines = normalizedPayload.slice(4, endIndex).split("\n");
  const metadata: Record<string, unknown> = {};

  for (const line of metadataLines) {
    const separatorIndex = line.indexOf(":");
    if (separatorIndex === -1) {
      continue;
    }

    const key = line.slice(0, separatorIndex).trim();
    const value = line.slice(separatorIndex + 1).trim();
    if (!key) {
      continue;
    }

    try {
      metadata[key] = JSON.parse(value);
    } catch {
      metadata[key] = value;
    }
  }

  return {
    metadata,
    text: normalizedPayload.slice(endIndex + "\n---\n".length)
  };
}

function parseStringMetadata(value: unknown): string {
  return typeof value === "string" ? value : "";
}

function parseNumberMetadata(value: unknown, fallback: number): number {
  return typeof value === "number" && Number.isFinite(value) ? value : fallback;
}

function parseOptionalNumberMetadata(value: unknown): number | undefined {
  return typeof value === "number" && Number.isFinite(value) ? value : undefined;
}

function parseNumberStringMetadata(value: unknown, fallback: number): number {
  if (typeof value === "number") {
    return parseNumberMetadata(value, fallback);
  }

  if (typeof value !== "string" || !value.trim()) {
    return fallback;
  }

  const parsedValue = Number(value);
  return Number.isFinite(parsedValue) ? parsedValue : fallback;
}

function parseOptionalNumberStringMetadata(value: unknown): number | undefined {
  if (typeof value === "number") {
    return parseOptionalNumberMetadata(value);
  }

  if (typeof value !== "string" || !value.trim()) {
    return undefined;
  }

  const parsedValue = Number(value);
  return Number.isFinite(parsedValue) ? parsedValue : undefined;
}

function parseDocumentStatus(value: unknown): LocalDocument["status"] {
  return value === "archived" || value === "trashed" ? value : "active";
}

function getTitleFromFileName(fileName = ""): string {
  return fileName
    .replace(/\.speechdraft\.json$/i, "")
    .replace(/\.whisperwriter\.json$/i, "")
    .replace(/\.json$/i, "")
    .replace(/\.md$/i, "")
    .trim();
}

function getAppPropertyQuery(key: string, value: string): string {
  return `appProperties has { key='${key}' and value='${value}' }`;
}

function parseDriveTime(value: string | undefined, fallback: number): number {
  if (!value) {
    return fallback;
  }

  const timestamp = Date.parse(value);
  return Number.isFinite(timestamp) ? timestamp : fallback;
}
